/* ==========================================================================
   Jelli — theme.js
   Core storefront behaviour: cart, drawers, header, reveals, accordions,
   predictive search, toasts. No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var theme = window.theme || {};
  var routes = theme.routes || {};
  var strings = theme.strings || {};

  /* ---------------------------------------------------------------- utils */

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function on(el, type, handler, options) {
    if (el) el.addEventListener(type, handler, options);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (chr) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[chr];
    });
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function rootPath() {
    var root = routes.root || '/';
    return root === '/' ? '' : root.replace(/\/$/, '');
  }

  /**
   * Formats an amount in cents using the shop's money format string.
   */
  function formatMoney(cents, format) {
    var value = typeof cents === 'string' ? cents.replace('.', '') : cents;
    var formatString = format || theme.moneyFormat || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'number' ? precision : 2;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;

      if (isNaN(number) || number == null) return '0';

      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    var match = formatString.match(placeholder);
    if (!match) return formatString;

    var formatted;
    switch (match[1]) {
      case 'amount':
        formatted = withDelimiters(value, 2);
        break;
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        formatted = withDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = withDelimiters(value, 0, ' ');
        break;
      case 'amount_with_period_and_space_separator':
        formatted = withDelimiters(value, 2, ' ', '.');
        break;
      default:
        formatted = withDelimiters(value, 2);
    }

    return formatString.replace(placeholder, formatted);
  }

  theme.formatMoney = formatMoney;

  /* --------------------------------------------------------------- toasts */

  function toast(message, iconId) {
    var stack = document.getElementById('ToastStack');
    if (!stack) return;

    var el = document.createElement('div');
    el.className = 'toast';
    el.innerHTML =
      '<svg class="icon" aria-hidden="true" focusable="false"><use href="#icon-' +
      escapeHtml(iconId || 'check') +
      '"></use></svg><span>' +
      escapeHtml(message) +
      '</span>';
    stack.appendChild(el);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.classList.add('is-visible');
      });
    });

    setTimeout(function () {
      el.classList.remove('is-visible');
      setTimeout(function () {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, 340);
    }, 3200);
  }

  theme.toast = toast;

  /* -------------------------------------------------------------- drawers */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var Drawer = {
    active: null,
    lastFocused: null,

    open: function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      if (Drawer.active && Drawer.active !== el) Drawer.close();

      Drawer.lastFocused = document.activeElement;
      Drawer.active = el;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');

      var focusTarget = el.querySelector('[data-drawer-focus]') || el.querySelector(FOCUSABLE);
      if (focusTarget) {
        setTimeout(function () {
          focusTarget.focus();
        }, 120);
      }
    },

    close: function () {
      var el = Drawer.active;
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      Drawer.active = null;
      if (Drawer.lastFocused && typeof Drawer.lastFocused.focus === 'function') {
        Drawer.lastFocused.focus();
      }
    },

    trap: function (event) {
      var el = Drawer.active;
      if (!el || event.key !== 'Tab') return;
      var items = $$(FOCUSABLE, el).filter(function (item) {
        return item.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  theme.Drawer = Drawer;

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      Drawer.open(opener.getAttribute('data-drawer-open'));
      return;
    }
    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      Drawer.close();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      if (Drawer.active) {
        Drawer.close();
      } else {
        var lightbox = $('.lightbox.is-open');
        if (lightbox) closeLightbox(lightbox);
      }
    }
    Drawer.trap(event);
  });

  /* ------------------------------------------------------------------ cart */

  var Cart = {
    state: null,

    get: function () {
      return fetch(routes.cart + '.js', { credentials: 'same-origin' })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          Cart.state = cart;
          Cart.render(cart);
          return cart;
        });
    },

    add: function (items, options) {
      options = options || {};
      var body = { items: items };
      if (options.attributes) body.attributes = options.attributes;

      return fetch(routes.cart_add, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || data.message || strings.cartError);
            return data;
          });
        })
        .then(function (data) {
          if (options.attributes) {
            return Cart.update({ attributes: options.attributes }).then(function () {
              return data;
            });
          }
          return data;
        })
        .then(function (data) {
          return Cart.get().then(function () {
            return data;
          });
        });
    },

    change: function (payload) {
      return fetch(routes.cart_change, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          Cart.state = cart;
          Cart.render(cart);
          return cart;
        });
    },

    update: function (payload) {
      return fetch(routes.cart_update, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (response) {
        return response.json();
      });
    },

    /**
     * Stores the discount in the session so it is applied at checkout, and
     * remembers it on the cart so the checkout link can carry it too.
     */
    applyDiscount: function (code) {
      if (!code) return Promise.resolve();
      return fetch(rootPath() + '/discount/' + encodeURIComponent(code), {
        credentials: 'same-origin'
      })
        .catch(function () {
          /* Non-blocking: the checkout link still carries the code. */
        })
        .then(function () {
          return Cart.update({ attributes: { _bundle_discount: code } });
        });
    },

    clearDiscount: function () {
      return Cart.update({ attributes: { _bundle_discount: '' } });
    },

    checkoutUrl: function (cart) {
      var code = cart && cart.attributes ? cart.attributes._bundle_discount : '';
      var checkout = rootPath() + '/checkout';
      if (!code) return checkout;
      return rootPath() + '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(checkout);
    },

    /* -------------------------------------------------------- rendering */

    render: function (cart) {
      Cart.renderCount(cart.item_count);

      var drawer = document.getElementById('CartDrawer');
      if (drawer) {
        var body = $('[data-cart-body]', drawer);
        var foot = $('[data-cart-foot]', drawer);
        if (body) body.innerHTML = Cart.itemsMarkup(cart);
        if (foot) foot.innerHTML = Cart.footMarkup(cart);
        Cart.renderProgress(drawer, cart);
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    },

    renderCount: function (count) {
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.classList.toggle('is-visible', count > 0);
        el.classList.remove('is-bumped');
        // Force reflow so the animation replays on every update.
        void el.offsetWidth;
        if (count > 0) el.classList.add('is-bumped');
      });
    },

    renderProgress: function (drawer, cart) {
      var wrap = $('[data-cart-progress]', drawer);
      if (!wrap) return;
      var threshold = parseInt(wrap.getAttribute('data-threshold'), 10) || 0;
      if (!threshold) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;
      var remaining = Math.max(threshold - cart.total_price, 0);
      var percent = Math.min((cart.total_price / threshold) * 100, 100);
      var label = $('[data-cart-progress-label]', wrap);
      var fill = $('[data-cart-progress-fill]', wrap);
      if (fill) fill.style.width = percent + '%';
      if (label) {
        label.innerHTML = remaining
          ? wrap.getAttribute('data-text-remaining').replace('[amount]', '<strong>' + formatMoney(remaining) + '</strong>')
          : wrap.getAttribute('data-text-unlocked');
      }
    },

    itemsMarkup: function (cart) {
      if (!cart.items.length) {
        var drawerEl = document.getElementById('CartDrawer');
        var emptyText = drawerEl ? drawerEl.getAttribute('data-empty-text') : 'Your cart is empty';
        var emptyCta = drawerEl ? drawerEl.getAttribute('data-empty-cta') : 'Start shopping';
        return (
          '<div class="cart-empty">' +
          '<svg class="icon" style="width:34px;height:34px" aria-hidden="true" focusable="false"><use href="#icon-bag"></use></svg>' +
          '<p class="h3">' +
          escapeHtml(emptyText) +
          '</p>' +
          '<a class="button" href="' +
          escapeHtml(routes.all_products) +
          '">' +
          escapeHtml(emptyCta) +
          '</a>' +
          '</div>'
        );
      }

      var rows = cart.items
        .map(function (item, index) {
          var image = item.image
            ? '<img src="' +
              escapeHtml(item.image.replace(/(\.[a-zA-Z]+)(\?|$)/, '_200x$1$2')) +
              '" alt="' +
              escapeHtml(item.product_title) +
              '" width="76" height="76" loading="lazy">'
            : '';

          var variant =
            item.variant_title && item.variant_title !== 'Default Title'
              ? '<div class="line-item__variant">' + escapeHtml(item.variant_title) + '</div>'
              : '';

          var props = '';
          if (item.properties) {
            props = Object.keys(item.properties)
              .filter(function (key) {
                return key.charAt(0) !== '_' && item.properties[key];
              })
              .map(function (key) {
                return '<div class="line-item__props">' + escapeHtml(item.properties[key]) + '</div>';
              })
              .join('');
          }

          var price =
            item.original_line_price > item.final_line_price
              ? '<span class="price price--on-sale"><span class="price__current">' +
                formatMoney(item.final_line_price) +
                '</span> <s class="price__compare">' +
                formatMoney(item.original_line_price) +
                '</s></span>'
              : '<span class="price">' + formatMoney(item.final_line_price) + '</span>';

          return (
            '<li class="line-item" data-line="' +
            (index + 1) +
            '">' +
            '<a class="line-item__media" href="' +
            escapeHtml(item.url) +
            '">' +
            image +
            '</a>' +
            '<div class="line-item__body">' +
            '<a class="line-item__title" href="' +
            escapeHtml(item.url) +
            '">' +
            escapeHtml(item.product_title) +
            '</a>' +
            variant +
            props +
            '<div class="line-item__row">' +
            '<div class="qty" data-qty>' +
            '<button class="qty__btn" type="button" data-line-minus aria-label="Decrease quantity">&minus;</button>' +
            '<input class="qty__input" type="number" min="0" value="' +
            item.quantity +
            '" data-line-input aria-label="Quantity">' +
            '<button class="qty__btn" type="button" data-line-plus aria-label="Increase quantity">+</button>' +
            '</div>' +
            price +
            '</div>' +
            '<button class="line-item__remove" type="button" data-line-remove>Remove</button>' +
            '</div>' +
            '</li>'
          );
        })
        .join('');

      return '<ul class="line-items">' + rows + '</ul>';
    },

    footMarkup: function (cart) {
      if (!cart.items.length) return '';

      var discount = cart.attributes && cart.attributes._bundle_discount ? cart.attributes._bundle_discount : '';
      var discountRow = discount
        ? '<div class="cart-discount"><span>' +
          escapeHtml(strings.discountApplied || 'Discount applied at checkout') +
          '</span><span class="bundle__code">' +
          escapeHtml(discount) +
          '</span></div>'
        : '';

      return (
        discountRow +
        '<div class="cart-total"><span>Subtotal</span><span>' +
        formatMoney(cart.total_price) +
        '</span></div>' +
        '<a class="button button--accent button--large button--full" href="' +
        escapeHtml(Cart.checkoutUrl(cart)) +
        '"><span class="button__label">Checkout</span></a>' +
        '<a class="cart-note" href="' +
        escapeHtml(routes.cart) +
        '">View cart</a>' +
        '<p class="cart-note">Shipping &amp; taxes calculated at checkout</p>'
      );
    }
  };

  theme.Cart = Cart;

  /* Line-item interactions inside the drawer (delegated). */
  document.addEventListener('click', function (event) {
    var item = event.target.closest('.line-item');
    if (!item) return;
    var line = parseInt(item.getAttribute('data-line'), 10);
    var input = $('[data-line-input]', item);
    if (!line || !input) return;

    if (event.target.closest('[data-line-remove]')) {
      event.preventDefault();
      item.classList.add('is-removing');
      setTimeout(function () {
        Cart.change({ line: line, quantity: 0 });
      }, 220);
      return;
    }

    if (event.target.closest('[data-line-plus]')) {
      Cart.change({ line: line, quantity: parseInt(input.value, 10) + 1 });
    } else if (event.target.closest('[data-line-minus]')) {
      Cart.change({ line: line, quantity: Math.max(parseInt(input.value, 10) - 1, 0) });
    }
  });

  document.addEventListener(
    'change',
    function (event) {
      var input = event.target.closest('[data-line-input]');
      if (!input) return;
      var item = input.closest('.line-item');
      var line = parseInt(item.getAttribute('data-line'), 10);
      var quantity = Math.max(parseInt(input.value, 10) || 0, 0);
      Cart.change({ line: line, quantity: quantity });
    },
    false
  );

  /* --------------------------------------------------------- add to cart */

  function setButtonState(button, state) {
    if (!button) return;
    button.classList.remove('is-loading', 'is-done');
    if (state) button.classList.add(state);
    button.disabled = state === 'is-loading';
  }

  theme.setButtonState = setButtonState;

  /**
   * Handles every [data-product-form] on the page, including quick-add forms
   * in product cards.
   */
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();

    var button = $('[data-add-button]', form);
    var errorEl = $('[data-form-error]', form);
    if (errorEl) errorEl.hidden = true;
    setButtonState(button, 'is-loading');

    var items = [];
    var bundle = form.bundleController;

    if (bundle && typeof bundle.getItems === 'function') {
      items = bundle.getItems();
    } else {
      var idInput = form.querySelector('[name="id"]');
      var qtyInput = form.querySelector('[name="quantity"]');
      if (!idInput || !idInput.value) {
        setButtonState(button, null);
        return;
      }
      items = [{ id: parseInt(idInput.value, 10), quantity: parseInt(qtyInput ? qtyInput.value : 1, 10) || 1 }];
    }

    if (!items.length) {
      setButtonState(button, null);
      return;
    }

    var discountCode = bundle && typeof bundle.getDiscountCode === 'function' ? bundle.getDiscountCode() : '';

    Cart.add(items)
      .then(function () {
        return discountCode ? Cart.applyDiscount(discountCode).then(Cart.get) : Cart.clearDiscount().then(Cart.get);
      })
      .then(function () {
        setButtonState(button, 'is-done');
        setTimeout(function () {
          setButtonState(button, null);
        }, 1600);

        if (theme.cartDrawer) {
          Drawer.open('CartDrawer');
        } else {
          toast(strings.itemAdded || 'Added to cart');
        }
      })
      .catch(function (error) {
        setButtonState(button, null);
        if (errorEl) {
          errorEl.textContent = error.message || strings.cartError || 'Something went wrong.';
          errorEl.hidden = false;
        } else {
          toast(error.message || strings.cartError || 'Something went wrong.', 'alert');
        }
      });
  });

  /* ----------------------------------------------------- header on scroll */

  function initHeader() {
    var group = $('.header-group');
    if (!group) return;
    var lastY = window.scrollY;
    var ticking = false;

    function update() {
      var y = window.scrollY;
      group.classList.toggle('is-stuck', y > 8);

      var announcement = $('.announcement', group);
      var threshold = announcement ? announcement.offsetHeight + 120 : 160;
      var goingDown = y > lastY && y > threshold;

      if (!Drawer.active) {
        group.classList.toggle('is-hidden', goingDown);
      }

      lastY = y;
      ticking = false;
    }

    on(window, 'scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });

    update();
  }

  /* ---------------------------------------------------------- reveal ---- */

  var revealObserver = null;

  function initReveals(scope) {
    var elements = $$('.reveal', scope || document).filter(function (el) {
      return !el.classList.contains('is-visible');
    });
    if (!elements.length) return;

    /* In the theme editor sections are injected and scrolled to
       programmatically, so the observer often never fires and the merchant is
       left staring at invisible content. Never animate in design mode. */
    var designMode = !!(window.Shopify && window.Shopify.designMode);

    if (designMode || prefersReducedMotion() || !('IntersectionObserver' in window)) {
      elements.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
    }

    elements.forEach(function (el, index) {
      if (!el.style.getPropertyValue('--reveal-delay')) {
        var group = el.getAttribute('data-reveal-group');
        if (group) el.style.setProperty('--reveal-delay', Math.min(index, 8) * 60 + 'ms');
      }
      revealObserver.observe(el);
      pending.push(el);
    });

    startSafetyNet();
  }

  /* IntersectionObserver samples at frame boundaries. During a fast flick, or
     on a device that drops frames, an element can pass through the viewport
     between two samples and stay invisible forever. This sweep guarantees that
     never happens: anything that has reached the viewport gets revealed. */
  var pending = [];
  var sweepScheduled = false;

  function sweepReveals() {
    sweepScheduled = false;
    if (!pending.length) return;
    var limit = window.innerHeight * 0.96;
    var remaining = [];
    for (var i = 0; i < pending.length; i++) {
      var el = pending[i];
      if (el.classList.contains('is-visible')) continue;
      if (el.getBoundingClientRect().top < limit) {
        el.classList.add('is-visible');
        if (revealObserver) revealObserver.unobserve(el);
      } else {
        remaining.push(el);
      }
    }
    pending = remaining;
  }

  function scheduleSweep() {
    if (sweepScheduled) return;
    sweepScheduled = true;
    window.requestAnimationFrame(sweepReveals);
  }

  /* A scroll-driven sweep can still be starved if the main thread is busy
     during a fast flick. A low-frequency timer backs it up and retires itself
     as soon as every reveal has been accounted for. */
  var sweepTimer = null;

  function startSweepTimer() {
    if (sweepTimer) return;
    sweepTimer = window.setInterval(function () {
      sweepReveals();
      if (!pending.length) {
        window.clearInterval(sweepTimer);
        sweepTimer = null;
      }
    }, 300);
  }

  var safetyNetStarted = false;

  function startSafetyNet() {
    if (safetyNetStarted) return;
    safetyNetStarted = true;
    window.addEventListener('scroll', scheduleSweep, { passive: true });
    window.addEventListener('resize', scheduleSweep, { passive: true });
    window.addEventListener('load', scheduleSweep);
    scheduleSweep();
    startSweepTimer();
  }

  theme.initReveals = initReveals;

  /* ------------------------------------------------------- accordions --- */

  function initAccordions(scope) {
    $$('[data-accordion]', scope || document).forEach(function (root) {
      if (root.dataset.accordionReady === 'true') return;
      root.dataset.accordionReady = 'true';
      var single = root.getAttribute('data-accordion') === 'single';

      $$('.accordion__trigger', root).forEach(function (trigger) {
        on(trigger, 'click', function () {
          var item = trigger.closest('.accordion__item');
          var isOpen = item.classList.contains('is-open');

          if (single) {
            $$('.accordion__item', root).forEach(function (other) {
              if (other !== item) {
                other.classList.remove('is-open');
                var otherTrigger = $('.accordion__trigger', other);
                if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
              }
            });
          }

          item.classList.toggle('is-open', !isOpen);
          trigger.setAttribute('aria-expanded', String(!isOpen));
        });
      });
    });
  }

  /* -------------------------------------------------- announcement bar -- */

  function initAnnouncement(scope) {
    $$('[data-rotator]', scope || document).forEach(function (rotator) {
      var slides = $$('.announcement__slide', rotator);
      if (slides.length < 2) return;
      var interval = parseInt(rotator.getAttribute('data-rotator-interval'), 10) || 4500;
      var index = 0;

      if (rotator.rotatorTimer) clearInterval(rotator.rotatorTimer);

      rotator.rotatorTimer = setInterval(function () {
        var current = slides[index];
        index = (index + 1) % slides.length;
        var next = slides[index];
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        next.classList.add('is-active');
        setTimeout(function () {
          current.classList.remove('is-leaving');
        }, 460);
      }, interval);
    });
  }

  /* ------------------------------------------------------- qty steppers - */

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-qty-change]');
    if (!btn) return;
    var wrap = btn.closest('[data-qty]');
    var input = $('input', wrap);
    if (!input) return;
    var step = btn.getAttribute('data-qty-change') === 'up' ? 1 : -1;
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    input.value = Math.max(parseInt(input.value, 10) + step, min);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* --------------------------------------------------- predictive search */

  function initSearch() {
    var drawer = document.getElementById('SearchDrawer');
    if (!drawer) return;
    var input = $('[data-search-input]', drawer);
    var results = $('[data-search-results]', drawer);
    if (!input || !results) return;

    var run = debounce(function () {
      var term = input.value.trim();
      if (term.length < 2) {
        results.innerHTML = '';
        return;
      }

      results.innerHTML = '<p class="text-muted">Searching&hellip;</p>';

      fetch(
        routes.predictive_search +
          '?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=6&section_id=predictive-search',
        { credentials: 'same-origin' }
      )
        .then(function (response) {
          if (!response.ok) throw new Error('search failed');
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var found = doc.querySelector('[data-predictive-results]');
          results.innerHTML = found ? found.innerHTML : '<p class="text-muted">No results found.</p>';
        })
        .catch(function () {
          results.innerHTML = '<p class="text-muted">No results found.</p>';
        });
    }, 260);

    on(input, 'input', run);
  }

  /* ------------------------------------------------------------ lightbox */

  function closeLightbox(lightbox) {
    lightbox.classList.remove('is-open');
    document.body.classList.remove('is-locked');
  }

  theme.closeLightbox = closeLightbox;

  /* --------------------------------------------------------------- init  */

  function init(scope) {
    initReveals(scope);
    initAccordions(scope);
    initAnnouncement(scope);
  }

  function boot() {
    init(document);
    initHeader();
    initSearch();
    Cart.get().catch(function () {
      /* Cart is non-critical on first paint. */
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify theme editor lifecycle. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    initHeader();
    initSearch();
    if (window.theme && typeof window.theme.initProduct === 'function') {
      window.theme.initProduct(event.target);
    }
  });

  document.addEventListener('shopify:section:select', function (event) {
    init(event.target);
  });

  window.theme = theme;
})();
